import React, { useEffect } from 'react';
import { Container } from '../components/ui/Container/Container';
import { PublicHeader } from '../components/Layout/PublicHeader';
import { PublicFooter } from '../components/Layout/PublicFooter';

export function CookiePolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />

      <main className="py-16">
        <Container size="md">
          <div className="bg-white rounded-lg shadow-sm p-8 md:p-12">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h1 className="text-4xl font-bold text-foreground mb-4">Cookie Policy</h1>
                <p className="text-lg text-muted-foreground">
                  Last updated: March 19, 2026
                </p>
              </div>

              <div className="prose prose-lg max-w-none">

                {/* 1. Introduction */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">1. What Are Cookies</h2>
                  <p className="text-muted-foreground">
                    Cookies are small text files stored on your device when you visit a website. They are widely used to make
                    websites work efficiently, remember your preferences, and provide usage information to site operators.
                    This policy explains what cookies Cascady uses, why we use them, and how you can manage them.
                  </p>
                </section>

                {/* 2. Essential Cookies */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">2. Essential Cookies</h2>
                  <p className="text-muted-foreground mb-4">
                    These cookies are strictly necessary for the Service to function. They cannot be disabled without
                    breaking core functionality.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 pr-4 text-foreground">Cookie</th>
                          <th className="text-left py-2 pr-4 text-foreground">Purpose</th>
                          <th className="text-left py-2 text-foreground">Duration</th>
                        </tr>
                      </thead>
                      <tbody className="text-muted-foreground">
                        <tr className="border-b">
                          <td className="py-2 pr-4"><code>refreshToken</code></td>
                          <td className="py-2 pr-4">Authentication — keeps you logged in</td>
                          <td className="py-2">7 days</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 pr-4"><code>accessToken</code></td>
                          <td className="py-2 pr-4">Session authentication token</td>
                          <td className="py-2">15 minutes</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* 3. Analytics Cookies */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">3. Analytics Cookies</h2>
                  <p className="text-muted-foreground mb-4">
                    We use Google Analytics to understand how visitors interact with our website. These cookies collect
                    information in an anonymized form, including the number of visitors, where they came from, and which
                    pages they visited.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 pr-4 text-foreground">Cookie</th>
                          <th className="text-left py-2 pr-4 text-foreground">Purpose</th>
                          <th className="text-left py-2 text-foreground">Duration</th>
                        </tr>
                      </thead>
                      <tbody className="text-muted-foreground">
                        <tr className="border-b">
                          <td className="py-2 pr-4"><code>_ga</code></td>
                          <td className="py-2 pr-4">Google Analytics — distinguishes users</td>
                          <td className="py-2">2 years</td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 pr-4"><code>_ga_*</code></td>
                          <td className="py-2 pr-4">Google Analytics — maintains session state</td>
                          <td className="py-2">2 years</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* 4. Preference Cookies */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">4. Preference Cookies</h2>
                  <p className="text-muted-foreground mb-4">
                    These cookies store your preferences and settings to enhance your experience on return visits.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 pr-4 text-foreground">Storage</th>
                          <th className="text-left py-2 pr-4 text-foreground">Purpose</th>
                          <th className="text-left py-2 text-foreground">Type</th>
                        </tr>
                      </thead>
                      <tbody className="text-muted-foreground">
                        <tr className="border-b">
                          <td className="py-2 pr-4"><code>localStorage</code></td>
                          <td className="py-2 pr-4">UI preferences, dismissed banners, theme settings</td>
                          <td className="py-2">Persistent</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* 5. Third-Party Cookies */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">5. Third-Party Cookies</h2>
                  <p className="text-muted-foreground">
                    When you interact with Stripe's payment forms (checkout, customer portal), Stripe may set its own cookies
                    for fraud prevention and payment processing. These are governed by{' '}
                    <a href="https://stripe.com/cookies-policy/legal" target="_blank" rel="noopener noreferrer" className="text-primary-600 underline">Stripe's Cookie Policy</a>.
                  </p>
                </section>

                {/* 6. Managing Cookies */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">6. Managing Cookies</h2>
                  <p className="text-muted-foreground mb-4">
                    You can control and manage cookies through your browser settings. Most browsers allow you to:
                  </p>
                  <ul className="list-disc pl-6 text-muted-foreground mb-4">
                    <li>View and delete existing cookies</li>
                    <li>Block cookies from specific or all websites</li>
                    <li>Set preferences for certain types of cookies</li>
                    <li>Get notifications when a site sets a cookie</li>
                  </ul>
                  <p className="text-muted-foreground">
                    Please note that disabling essential cookies will prevent you from logging in and using the Service.
                    Disabling analytics cookies will not affect your ability to use the platform.
                  </p>
                </section>

                {/* 7. Contact */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">7. Contact</h2>
                  <p className="text-muted-foreground mb-4">
                    If you have questions about our use of cookies, please contact us:
                  </p>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-muted-foreground mb-2"><strong>Email:</strong> andrej@zeropointstudio.io</p>
                    <p className="text-muted-foreground"><strong>Address:</strong> Rudeška cesta 179, 10000 Zagreb, Croatia</p>
                  </div>
                </section>

              </div>
            </div>
          </div>
        </Container>
      </main>

      <PublicFooter />
    </div>
  );
}
